import React, { useMemo, useEffect, useState } from "react";
import Table from "../../../Components/Table/index";
import axios from "axios";
import { API_CONSTANTS } from "../../../API_CONSTANTS";
import Spinner from "react-bootstrap/Spinner";
import moment from "moment";
import { useSelector } from "react-redux";
import PatientFilterForm from "./PatientFilterForm";
import AlertMessage from "../../../Components/AlertMessage";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Patient() {
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState("");
  const [patientList, setPatientList] = useState([]);
  const [patientSearch, setPatientSearch] = useState({
    number: "",
    countryCode: "+92",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false); // New state for export loading
  const fetchIdRef = React.useRef(0);

  const filter_status = {
    gender: useSelector((state) => state.patientFilterReducer.gender),
    created_at: useSelector((state) => state.patientFilterReducer.value2),
    appoint_date_from: useSelector(
      (state) => state.patientFilterReducer.value3
    ),
    os_name: useSelector((state) => state.patientFilterReducer.os_name),
    user_type: useSelector((state) => state.patientFilterReducer.user_type),
    recurring: useSelector((state) => state.patientFilterReducer.recurring),
  };
  const handleFilters = () => {
    let filterData = {
      gender: `${filter_status.gender ? filter_status.gender : ""}`,
      created_at: `${filter_status.created_at ? filter_status.created_at : ""}`,
      appoint_date_from: `${
        filter_status.appoint_date_from ? filter_status.appoint_date_from : ""
      }`,
      os_name: `${filter_status.os_name ? filter_status.os_name : ""}`,
      user_type: `${filter_status.user_type ? filter_status.user_type : ""}`,
      recurring: `${filter_status.recurring ? filter_status.recurring : ""}`,
    };
    if (Object.values(filterData).every((key) => key === "")) {
      return {};
    } else
      return Object.fromEntries(
        Object.entries(filterData).filter(([key, value]) => value !== "")
      );
  };

  const { number, countryCode } = patientSearch;
  const fetchData = React.useCallback(
    ({ pageSize, pageIndex }) => {
      const fetchId = lastEvaluatedKey;
      if (fetchId === fetchIdRef.current) {
        const startRow = pageSize * pageIndex;
        const endRow = startRow + pageSize;
        setPatientList(patientList.slice(startRow, endRow));
        setPageCount(Math.ceil(patientList.length / pageSize));
      }
    },
    [patientList, lastEvaluatedKey]
  );

  const userList = () => {
    setIsLoading(false);
    axios
      .post(`${API_CONSTANTS.BASEURL}admin/patients`, handleFilters(), {
        headers: API_CONSTANTS.headers(),
      })
      .then((res) => {
        setIsLoading(true);
        setLastEvaluatedKey(res.data.response.LastEvaluatedKey);
        setPatientList(res.data.response.Items);
      })
      .catch((error) => {
        if (error.response.status === 401) {
          window.location.reload();
          window.localStorage.clear();
        }
        setIsLoading(true);
        setPatientList([]);
      });
  };

  const handleSearchInput = (event, state) => {
    if (event.reset) {
      return { number: "", countryCode: "+92" };
    }
    return setPatientSearch({
      ...state,
      number: event.target.value.slice(0, event.target.maxLength),
      countryCode: "+92",
    });
  };

  const handleKeyPress = (event) => {
    if (
      event.key === "Enter" &&
      patientSearch.number[0] === "3" &&
      patientSearch.number.length === 10
    ) {
      handlePatientSearch();
    }
  };

  const handlePatientSearch = () => {
    axios
      .get(
        `${API_CONSTANTS.BASEURL_1}admin/patientSearch/${patientSearch.countryCode}${patientSearch.number}`,
        {
          headers: API_CONSTANTS.headers(),
        }
      )
      .then((res) => setPatientList(res.data.response.Items))
      .then(() => setPatientSearch({ countryCode: "+92", number: "" }))
      .catch((error) => alert(error.response.data.response.message));
  };

  const [page, setPage] = useState(0);

  const handleLoadMoreData = () => {
    axios
      .post(
        `${API_CONSTANTS.BASEURL}admin/patients?last=${lastEvaluatedKey}`,
        handleFilters(),
        {
          headers: API_CONSTANTS.headers(),
        }
      )
      .then((response) => {
        setLastEvaluatedKey(response.data.response.LastEvaluatedKey);
        var patientListNextPage = response.data.response.Items;
        setPatientList([...patientList, ...patientListNextPage]);
        setPage(page + 1);
      })
      .then(() => setLoading(true));
  };

  const onDownloadBtn = async () => {
    const filters = handleFilters();
    if (!Object.keys(filters).length) {
      alert("Please select valid filters before proceeding.");
      return;
    }

    setIsExporting(true); // Start loading

    try {
      const response = await axios.post(
        `${API_CONSTANTS.BASEURL}admin/patients?loadall=true`,
        filters,
        {
          headers: API_CONSTANTS.headers(),
        }
      );

      if (response.data && response.data.response) {
        toast.success("Export started successfully. Please wait...");
        const s3Url = response.data.response.s3_url;
        if (s3Url) {
          const link = document.createElement("a");
          link.href = s3Url;
          link.download = "consultation.csv"; // Set the desired file name
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        toast.error("No data found for download");
      }
    } catch (error) {
      console.error("An error occurred:", error);
      toast.error("An error occurred while downloading");
    } finally {
      setIsExporting(false); // Stop loading
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: (property) =>
          property.Fname
            ? property.Fname.charAt(0).toUpperCase() + property.Fname.slice(1)
            : "UnNamed",
      },
      {
        Header: "Phone Number",
        accessor: "phoneNo",
      },
      {
        Header: "Gender",
        accessor: "gender",
        Cell: ({ cell: { value } }) => value || "N/A",
      },
      {
        Header: "Age",
        accessor: (patientList) =>
          !patientList.dob
            ? ""
            : moment().diff(patientList.dob, "years") + " years",
        Cell: ({ cell: { value } }) => value || "N/A",
      },
      {
        Header: "Type",
        accessor: (item) => (item.corporateUser ? "Corporate" : "Personal"),
      },
      {
        Header: "City",
        accessor: "location",
      },
      {
        Header: "Date",
        accessor: (item) =>
          moment(item.created_at, "YYYY-MM-DD").format("D-MM-YYYY"),
      },
      {
        Header: "Time",
        accessor: (item) => moment(item.created_at).format("h:mm A"),
      },
      {
        Header: "Total Appointment",
        accessor: (totalAppointmentCount) => {
          if (totalAppointmentCount.totalAppointmentCount >= 1) {
            return totalAppointmentCount.totalAppointmentCount;
          } else {
            return "-";
          }
        },
        Cell: ({ value }) => <div style={{ paddingLeft: "7px" }}>{value}</div>,
      },
      {
        Header: "Device Name",
        accessor: "os_name",
      },
    ],
    []
  );

  const is_SessionExpire = () => {
    window.localStorage.clear();
    window.location.reload();
  };

  const patientListCheck = () => {
    if (patientList.length === 1) {
      userList();
    }
  };

  useEffect(() => {
    userList();
  }, [
    filter_status.created_at,
    filter_status.gender,
    filter_status.appoint_date_from,
    filter_status.os_name,
    filter_status.user_type,
    filter_status.recurring,
  ]);

  if (error) {
    return (
      <AlertMessage
        errorMessage={
          error.message === "Request failed with status code 404"
            ? "This record does not exist"
            : error.message === "Request failed with status code 401"
            ? is_SessionExpire()
            : error.message
        }
      />
    );
  } else {
    return (
      <>
        <ToastContainer autoClose={2000} />
        {/* Full-screen loading overlay */}
        {isExporting && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <Spinner
              animation="border"
              role="status"
              style={{ color: "rgb(0, 155, 148)" }}
            >
              <span className="visually-hidden ">Loading...</span>
            </Spinner>
          </div>
        )}
        <div className="patient">
          <div className="input-group mt-3 pe-4 ms-4" style={{ width: "50%" }}>
            <div className="form-outline d-flex">
              <select
                name="countryCode"
                style={{
                  borderRadius: "7px 0 0 7px",
                  borderRight: "3px solid #009B94",
                  background: "#009B9414",
                  color: "#4E4E4E99",
                }}
                defaultValue={countryCode}
              >
                <option selected onChange={handleSearchInput}>
                  +92
                </option>
              </select>
              <input
                type="number"
                id="form1"
                className="form-control patient-search-input"
                placeholder="Search"
                value={number}
                name="number"
                onChange={handleSearchInput}
                onKeyPress={handleKeyPress}
                maxLength="10"
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={handlePatientSearch}
              disabled={
                patientSearch.number.slice(0, 1) === "3" &&
                patientSearch.number.length === 10
                  ? false
                  : true
              }
              style={{ backgroundColor: "rgb(0, 155, 148)", color: "#fff" }}
            >
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>
        <div className="row rounded bg-white m-4">
          <div className="d-flex align-items-center">
            <div style={{ padding: "18px" }}>Patients</div>
            {!isLoading && (
              <div className="d-flex mb-3" style={{ padding: "18px 0  0" }}>
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ color: "#24AAA4" }}
                />
              </div>
            )}
          </div>
          <Table
            pageNumber={page}
            columns={columns}
            data={patientList}
            routingLink={"/patients/profile"}
            pageCount={pageCount}
            fetchData={fetchData}
            loadMoreList={handleLoadMoreData}
            lastEvaluatedKey={lastEvaluatedKey}
            loading={loading}
            recordDownload={false}
            setLoading={setLoading}
            FilterForm={<PatientFilterForm patientList={patientListCheck} />}
            onDownloadBtn={onDownloadBtn}
          />
        </div>
        <style jsx="true">
          {`
            .patient {
              display: flex;
              justify-content: space-between;
            }
            .patient-search-input {
              border-radius: 0;
            }
            .form-control:focus {
              background-color: #fff;
              border-color: rgb(0, 155, 148) !important;
              box-shadow: 0 0 0 0.25rem rgb(0 155 148 / 25%) !important;
            }
            .btn:focus {
              box-shadow: 0 0 0 0.25rem rgb(0 155 148 / 25%) !important;
            }
            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
          `}
        </style>
      </>
    );
  }
}
